/**
 * The recorded room's player, as the confirmed prototype draws it: the picture with the host's words on it, tap
 * to pause and double tap for ten seconds on a phone, moments that hold the talk inside the picture, the offer in its
 * corner, the end card; under it the controls: a filmstrip of the chapters, the bar with the chapters as segments
 * and the moments as dots, a frame under the pointer, and the row of buttons with the chapters and speed menus.
 *
 * The page holds the state (`RoomPage`); this draws it and reports what the viewer does.
 */
import { type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { HeroFrames, WatchCaption, WatchInteraction, WatchSession } from "../../../shared/types";
import type { AnswerFn, Arrivals } from "../TalkPlayer";
import { GIcon } from "./GIcon";
import { OfferCard, type OfferProps, OfferTab } from "./RoomOffer";
import { heldWords, RoomMoment } from "./RoomMoment";
import { captionAt, chapterAt, chapterEnd, frameStyle, holdsTalk, lineAt, menuRoom, mmss, SPEEDS, speedLabel } from "./talk";
import type { RoomVideo } from "./useRoomVideo";

/** A picture at least this tall has the room for a moment and the open offer inside it. */
export const ROOMY_PX = 380;
/** The space a menu keeps from the edge it would meet: the screen's, the dock's or the top bar's. */
const MENU_GAP = 8;

const TICK = (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

function momentName(m: WatchInteraction, host: string): string {
  if (m.type === "poll") return "A quick poll";
  if (m.type === "question") return "A quick question";
  if (m.type === "reaction") return `${host}’s moment`;
  return `A handout from ${host}`;
}

/** One chapter as a frame: the filmstrip in the controls, and the list under the player. */
export function ChapterFrame({
  t,
  title,
  on,
  frames,
  poster,
  onJump,
}: {
  t: number;
  title: string;
  on: boolean;
  frames: HeroFrames | null;
  poster: string | null;
  onJump: (t: number) => void;
}): ReactNode {
  const pic = frames ? frameStyle(frames, t + 2) : poster ? { backgroundImage: `url("${poster}")` } : undefined;
  return (
    <button className={on ? "fr on" : "fr"} type="button" onClick={() => onJump(t)}>
      <span className="fr-t tnum">{mmss(t)}</span>
      <span className="fr-pic" style={pic} />
      <span className="fr-n">
        {title}
        <span className="now"> · Now</span>
      </span>
    </button>
  );
}

interface RoomPlayerProps {
  session: WatchSession;
  v: RoomVideo;
  host: string;
  hostAvatar: string | null;
  frames: HeroFrames | null;
  captions: readonly WatchCaption[];
  cc: boolean;
  onCc: () => void;
  arrivals: Arrivals;
  onAnswer: AnswerFn;
  /** A moment's dot pressed: one not yet answered may arrive again as the talk crosses it. */
  onAgain: (id: string) => void;
  /** The overlay over the picture: the big play, welcome back, or the end. */
  over: ReactNode;
  /** The offer: has it landed, and is it open. */
  offer: { seen: boolean; open: boolean } | null;
  offerProps: OfferProps | null;
  onOffer: (open: boolean) => void;
  fs: boolean;
  onFs: () => void;
  picRef: RefObject<HTMLDivElement | null>;
  menu: "spd" | "chs" | null;
  setMenu: (m: "spd" | "chs" | null) => void;
  /** Something the viewer did: keeps the controls awake. */
  poke: () => void;
  /** The picture's taps, left and right: the ten-second flash. */
  tap: { side: "l" | "r"; n: number } | null;
  onTap: (side: "l" | "r") => void;
  floats: ReadonlyArray<{ id: number; emoji: string; left: number; dx: number }>;
  onFloat: (emoji: string) => void;
  roomy: boolean;
}

export function RoomPlayer(p: RoomPlayerProps): ReactNode {
  const { session, v, host, frames, captions, cc, arrivals, offer, offerProps } = p;
  const total = v.total;
  const chapters = session.chapters;
  const ci = chapterAt(chapters, v.cur);
  const moment = arrivals.up;
  const heldNow = !!moment && holdsTalk(arrivals);
  const lastTap = useRef(0);
  const tapTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(tapTimer.current), []);

  // The bar: drag to move, a frame under the pointer, the keyboard's arrows.
  const scrub = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState(false);
  const [tip, setTip] = useState<{ x: number; t: number } | null>(null);
  const tAt = (clientX: number) => {
    const r = scrub.current?.getBoundingClientRect();
    if (!r || r.width === 0) return 0;
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width)) * total;
  };

  // The open menu, measured where it lands: it turns up or scrolls rather than run under the dock or off the screen.
  const menuBox = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = menuBox.current;
    if (!el) return;
    el.classList.remove("up");
    el.style.maxHeight = "";
    const opensUp = getComputedStyle(el).top === "auto";
    const box = el.getBoundingClientRect();
    const room = el.closest(".rm");
    const dock = room?.querySelector(".rm-dock");
    const bar = room?.querySelector(".rm-top");
    const floor = dock && dock.getClientRects().length > 0 ? dock.getBoundingClientRect().top : window.innerHeight;
    const ceiling = opensUp ? (p.picRef.current?.getBoundingClientRect().top ?? 0) : Math.max(0, bar?.getBoundingClientRect().bottom ?? 0);
    const from = opensUp ? box.bottom : (el.closest(".rm-ctrl")?.getBoundingClientRect().top ?? box.top);
    const fit = menuRoom(box.height, floor - box.top - MENU_GAP, from - ceiling - MENU_GAP, opensUp);
    el.classList.toggle("up", fit.flip);
    if (fit.max !== null) el.style.maxHeight = `${fit.max}px`;
  }, [p.menu, p.picRef]);

  const small = !p.roomy;
  const offerCard =
    offer?.seen && offerProps ? (
      offer.open ? (
        <OfferCard {...offerProps} onFold={() => p.onOffer(false)} />
      ) : (
        <OfferTab {...offerProps} small={small} onOpen={() => p.onOffer(true)} />
      )
    ) : null;
  const said = moment ? lineAt(captions, moment.appears_at)?.text ?? null : null;
  const picClass = ["rm-pic", p.fs ? "fs" : "", small ? "small" : "", moment ? "mo-on" : "", moment && p.roomy ? "mo-in" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={picClass} ref={p.picRef}>
      <div className={heldNow ? "rm-frame held" : "rm-frame"}>
        <video
          className="rm-v"
          ref={v.videoRef}
          poster={session.poster_url ?? undefined}
          playsInline
          preload="auto"
          {...v.events}
        />
        <div className="rm-cap" aria-live="off">
          {cc ? captionAt(captions, v.cur) : ""}
        </div>
        <button
          className="rm-hit"
          type="button"
          aria-label="Play or pause"
          onPointerUp={(e) => {
            if (e.pointerType === "mouse") {
              v.toggle();
              return;
            }
            const now = performance.now();
            const r = e.currentTarget.getBoundingClientRect();
            const x = (e.clientX - r.left) / r.width;
            if (now - lastTap.current < 320 && (x < 0.4 || x > 0.6)) {
              window.clearTimeout(tapTimer.current);
              lastTap.current = now;
              p.onTap(x < 0.5 ? "l" : "r");
              return;
            }
            lastTap.current = now;
            window.clearTimeout(tapTimer.current);
            tapTimer.current = window.setTimeout(() => {
              if (performance.now() - lastTap.current >= 300) v.toggle();
            }, 320);
          }}
          onClick={(e) => {
            if (e.detail === 0) v.toggle();
          }}
        />
        <div className={p.tap?.side === "l" ? "rm-tap l go" : "rm-tap l"} key={`l${p.tap?.side === "l" ? p.tap.n : 0}`}>
          <span>
            <GIcon name="replay" size={26} />
            10 seconds
          </span>
        </div>
        <div className={p.tap?.side === "r" ? "rm-tap r go" : "rm-tap r"} key={`r${p.tap?.side === "r" ? p.tap.n : 0}`}>
          <span>
            <GIcon name="chr" size={26} />
            10 seconds
          </span>
        </div>
        <div className="rm-held" aria-hidden="true">
          <span>
            {p.hostAvatar ? <img className="av" src={p.hostAvatar} alt="" /> : null}
            <GIcon name="pause" size={14} />
            <b>{moment ? heldWords(moment.type, host) : ""}</b>
          </span>
          <q>{said ? `“${said}”` : ""}</q>
        </div>
        <div>{p.over}</div>
        <div>{p.roomy ? offerCard : offer?.seen && offerProps && !offer.open ? offerCard : null}</div>
        {p.floats.map((f) => (
          <span key={f.id} className="fl-e mine" style={{ left: `${f.left}%`, zIndex: 8, ["--dx" as string]: `${f.dx}px` }}>
            {f.emoji}
          </span>
        ))}
        {v.failed ? (
          <div className="rm-big">
            <p>The talk won’t play here right now. Try again in a moment.</p>
          </div>
        ) : null}
      </div>
      {moment ? (
        <RoomMoment
          key={moment.id}
          moment={moment}
          held={heldNow}
          inside={p.roomy}
          takesKeys={p.fs || (typeof matchMedia !== "undefined" && matchMedia("(min-width:1024px)").matches)}
          host={host}
          hostAvatar={p.hostAvatar}
          said={said}
          onAnswer={p.onAnswer}
          onSkip={arrivals.dismiss}
          onFloat={p.onFloat}
        />
      ) : null}
      <div className={p.menu ? "rm-ctrl menu-on" : "rm-ctrl"}>
        {chapters.length > 0 ? (
          <div className="rm-film" style={{ gridTemplateColumns: `repeat(${chapters.length},minmax(0,1fr))` }}>
            {chapters.map((c, i) => (
              <ChapterFrame key={`${c.t}-${c.title}`} t={c.t} title={c.title} on={i === ci} frames={frames} poster={session.poster_url} onJump={(t) => v.seek(t)} />
            ))}
          </div>
        ) : null}
        <div
          className={drag ? "rm-scrub drag" : "rm-scrub"}
          ref={scrub}
          role="slider"
          aria-label="Where you are in the talk"
          aria-valuemin={0}
          aria-valuemax={Math.round(total)}
          aria-valuenow={Math.floor(v.cur)}
          aria-valuetext={mmss(v.cur)}
          tabIndex={0}
          onPointerDown={(e) => {
            if ((e.target as HTMLElement).closest("[data-dot]")) return;
            setDrag(true);
            e.currentTarget.setPointerCapture?.(e.pointerId);
            v.seek(tAt(e.clientX));
          }}
          onPointerMove={(e) => {
            const t = tAt(e.clientX);
            if (drag) v.seek(t);
            if (e.pointerType === "mouse") {
              const r = e.currentTarget.getBoundingClientRect();
              setTip({ x: Math.max(98, Math.min(r.width - 98, e.clientX - r.left)), t });
            }
          }}
          onPointerLeave={() => setTip(null)}
          onPointerUp={() => setDrag(false)}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") {
              v.seek(v.cur + 5);
              e.preventDefault();
              e.stopPropagation();
            }
            if (e.key === "ArrowLeft") {
              v.seek(v.cur - 5);
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <div className="rm-segs">
            {(chapters.length > 0 ? chapters : [{ t: 0, title: "", sub: "" }]).map((c, i, all) => {
              const end = chapterEnd(all, i, total);
              const f = Math.max(0, Math.min(1, (v.cur - c.t) / Math.max(1, end - c.t)));
              return (
                <span key={`${c.t}-${i}`} className="rm-seg" style={{ flex: `${Math.max(1, end - c.t)} 1 0` }}>
                  <i style={{ width: `${f * 100}%` }} />
                </span>
              );
            })}
          </div>
          {session.interactions.map((m) => {
            const ans = m.my_response !== null;
            return (
              <button
                key={m.id}
                className={ans ? "rm-dot ans" : "rm-dot"}
                type="button"
                data-dot={m.id}
                style={{ left: `${((m.appears_at / total) * 100).toFixed(3)}%` }}
                aria-label={`${momentName(m, host)} at ${mmss(m.appears_at)}${ans ? ", answered" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  v.seek(Math.max(0, m.appears_at - 3));
                  p.onAgain(m.id);
                  v.play();
                }}
              >
                {TICK}
              </button>
            );
          })}
          {session.offer && typeof session.offer.appears_at === "number" && session.offer.appears_at > 0 ? (
            <button
              className={offer?.seen ? "rm-dot offer done" : "rm-dot offer"}
              type="button"
              data-dot="offer"
              style={{ left: `${((session.offer.appears_at / total) * 100).toFixed(3)}%` }}
              aria-label={`${host}’s offer at ${mmss(session.offer.appears_at)}`}
              onClick={(e) => {
                e.stopPropagation();
                if (offer?.seen) p.onOffer(true);
                else {
                  v.seek(Math.max(0, (session.offer?.appears_at ?? 0) - 3));
                  v.play();
                }
              }}
            />
          ) : null}
          <span className="rm-head" style={{ left: `${(v.cur / total) * 100}%` }} />
          <div className={tip ? "rm-tip on" : "rm-tip"} style={tip ? { left: `${tip.x}px` } : undefined}>
            <span className="fr-pic" style={tip && frames ? frameStyle(frames, tip.t) : session.poster_url ? { backgroundImage: `url("${session.poster_url}")` } : undefined} />
            <b className="tnum">{mmss(tip?.t ?? 0)}</b>
            <span>{chapters[chapterAt(chapters, tip?.t ?? 0)]?.title ?? ""}</span>
          </div>
        </div>
        <div className="rm-bar">
          <button className="ibtn play" type="button" aria-label={v.playing ? "Pause" : "Play"} onClick={v.toggle}>
            <GIcon name={v.playing ? "pause" : "play"} size={22} />
          </button>
          <button className="ibtn lap" type="button" aria-label="Back 10 seconds" title="Back 10 seconds (J)" onClick={() => p.onTap("l")}>
            <GIcon name="replay" size={21} />
          </button>
          <button className="ibtn lap" type="button" aria-label="Forward 10 seconds" title="Forward 10 seconds (L)" onClick={() => p.onTap("r")}>
            <GIcon name="chr" size={22} />
          </button>
          <span className="rm-time tnum">
            <b>{mmss(v.cur)}</b> / {mmss(total)}
          </span>
          <span className="rm-chap">{chapters[ci]?.title ?? ""}</span>
          <span className="rm-sp" />
          {chapters.length > 0 ? (
            <button
              className="ibtn"
              type="button"
              aria-label="Chapters"
              title="Chapters"
              aria-expanded={p.menu === "chs"}
              onClick={(e) => {
                e.stopPropagation();
                p.setMenu(p.menu === "chs" ? null : "chs");
              }}
            >
              <GIcon name="list" size={22} />
            </button>
          ) : null}
          {captions.length > 0 ? (
            <button
              className={cc ? "ibtn cc on" : "ibtn cc"}
              type="button"
              aria-label={`${host}’s words on screen`}
              aria-pressed={cc}
              title={`${host}’s words (C)`}
              onClick={p.onCc}
            >
              <GIcon name="cc" size={22} />
            </button>
          ) : null}
          <button
            className="rm-speed tnum"
            type="button"
            aria-label="Speed"
            aria-expanded={p.menu === "spd"}
            onClick={(e) => {
              e.stopPropagation();
              p.setMenu(p.menu === "spd" ? null : "spd");
            }}
          >
            {speedLabel(v.speed)}
          </button>
          <button className="ibtn lap" type="button" aria-label={v.muted ? "Unmute" : "Mute"} title="Mute (M)" onClick={v.toggleMute}>
            <GIcon name={v.muted ? "mute" : "vol"} size={22} />
          </button>
          <button className="ibtn" type="button" aria-label={p.fs ? "Leave full screen" : "Full screen"} title="Full screen (F)" onClick={p.onFs}>
            <GIcon name={p.fs ? "unfull" : "full"} size={22} />
          </button>
        </div>
        <div>
          {p.menu === "spd" ? (
            <div className="rm-menu" role="menu" ref={menuBox}>
              <p>Speed</p>
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  role="menuitemradio"
                  aria-checked={s === v.speed}
                  className={s === v.speed ? "on" : undefined}
                  onClick={() => {
                    v.setSpeed(s);
                    p.setMenu(null);
                  }}
                >
                  {speedLabel(s)}
                  {s === 1 ? <span className="muted"> Normal</span> : null}
                  {s === v.speed ? <GIcon name="check" size={16} /> : null}
                </button>
              ))}
            </div>
          ) : null}
          {p.menu === "chs" ? (
            <div className="rm-menu chaps" role="menu" ref={menuBox}>
              <p>Chapters</p>
              {chapters.map((c, i) => (
                <button
                  key={`${c.t}-${c.title}`}
                  type="button"
                  role="menuitem"
                  className={i === ci ? "on" : undefined}
                  onClick={() => {
                    p.setMenu(null);
                    v.seek(c.t);
                    v.play();
                  }}
                >
                  <em className="tnum">{mmss(c.t)}</em>
                  <span style={{ flex: 1 }} dir="auto">{c.title}</span>
                  {i === ci ? <GIcon name="play" size={14} /> : null}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
      <div className="rm-offout">{!p.roomy && offer?.seen && offer.open && offerProps ? offerCard : null}</div>
    </div>
  );
}
